import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AuthentifiedPage from "./AuthentifiedPage";
import { useAuth } from "../context/AuthContext";
import { rapportGenereService } from "../services/RapportGenereService";
import { RapportGenere } from "../types/RapportGenere";
import { codeToRapportStatus } from "../utils/rapportStatus";

// Couleurs pour chaque statut
const statutColor: Record<number, string> = {
  0: "#6c757d",
  1: "#0d6efd",
  2: "#ffc107",
  3: "#198754"
};

const regions = ["Toutes les régions", "Grande Comore", "Anjouan", "Mohéli"];
const RapportsGeneresPage: React.FC = () => {
  const navigate = useNavigate();
  const [rapports, setRapports] = useState<RapportGenere[]>([]);
  const [query, setQuery] = useState("");
  const [statutFilter, setStatutFilter] = useState<number>(-1);
  const [regionFilter, setRegionFilter] = useState<string>("Toutes les régions");
  const [page, setPage] = useState(1);
  const [actionMenu, setActionMenu] = useState<{ id: number; x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pageSize = 10;

  const { api, user } = useAuth();

  const changeRapportStatus = async (idRapportGenere: number, newStatus: number) => {
    if (!api) return null;
    try {
      const updated = await rapportGenereService.updateStatus(api, idRapportGenere, newStatus);
      if (updated) {
        const newList = rapports.map(r => (r.idRapportGenere === idRapportGenere) ? { ...r, statut: newStatus } : r);
        setRapports(newList);
        return updated;
      } else {
        return null;
      }
    } catch (e) {
      console.error("Erreur lors de la mise à jour du statut:", e);
      return null;
    }
  };

  useEffect(() => {
    const fetchRapports = async () => {
      if (!api) return;

      try {
        const data = await rapportGenereService.getAllRapportsGeneres(api);
        setRapports(data);
      } catch (e) {
        console.error("Erreur lors de la récupération des rapports générés :", e);
      }
    };

    fetchRapports();
  }, [api]);

  // Fermer le menu flottant si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setActionMenu(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();

    return rapports.filter(r => {
      const titre = (r.sousFenetre?.nomSousFenetre ?? r.fenetre?.nomFenetre ?? "").toLowerCase();
      const description = (r.rapport?.description ?? "").toLowerCase();
      //const region = (r.region ?? "").toLowerCase();

      const matchesStatut = statutFilter === -1 || r.statut === statutFilter;
      //const matchesRegion = regionFilter === "Toutes les régions" || r.idRegion === regionFilter;
      const matchesRegion = true;
      return matchesStatut && matchesRegion;
    });
  }, [rapports, query, statutFilter, regionFilter]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const deleteRapport = (id: number) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce rapport ?")) return;
    const newList = rapports.filter(r => r.idRapportGenere !== id);
    setRapports(newList);
    localStorage.setItem("rapportsGeneres", JSON.stringify(newList));
    setActionMenu(null);
  };

  const openRapport = (r: RapportGenere) => {
    navigate("/rapport-detail", { state: { modele: r } });
    setActionMenu(null);
  };

  const publierRapport = async (r: RapportGenere, type: "interne" | "public") => {
    const newStatut = type === "interne" ? 2 : 3;
    await changeRapportStatus(r.idRapportGenere, newStatut);
    setActionMenu(null);
  };

  const validerRapport = async (r: RapportGenere) => {
    // only allow if user roleRank >= 2
    if (!user || (user.roleRank ?? 0) < 2) {
      alert("Vous n'avez pas la permission de valider ce rapport.");
      return;
    }
    await changeRapportStatus(r.idRapportGenere, 1);
    setActionMenu(null);
  };

  return (
    <AuthentifiedPage>
      <main className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 className="text-success fw-bold mb-0">Rapports générés</h4>
        </div>

        {/* Barre de recherche + filtres */}
        <div className="filters-bar mb-3 d-flex flex-wrap gap-2">
          <div className="input-wrapper">
            <span className="search-icon"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <select className="custom-select" value={statutFilter} onChange={e => setStatutFilter(Number(e.target.value))}>
            <option value={-1}>Tous les statuts</option>
            <option value={0}>Créé</option>
            <option value={1}>Validé</option>
            <option value={2}>Publié Interne</option>
            <option value={3}>Publié Public</option>
          </select>
          <select className="custom-select" value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
            {regions.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* Tableau des rapports */}
        {filtered.length === 0 ? (
          <p>Aucun rapport généré pour le moment.</p>
        ) : (
          <div className="table-responsive shadow-sm">
            <table className="table table-hover align-middle">
              <thead className="table-success">
                <tr>
                  <th>ID</th>
                  <th>Fenêtre</th>
                  <th>Sous-Fenêtre</th>
                  <th>Description</th>
                  <th>Région</th>
                  <th>Statut</th>
                  <th>Date création</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageData.map(r => (
                  <tr key={r.idRapportGenere}>
                    <td>{r.idRapportGenere}</td>
                    <td>{r.fenetre?.nomFenetre || "-"}</td>
                    <td>{r.sousFenetre?.nomSousFenetre || "-"}</td>
                    <td>{r.rapport?.description || "-"}</td>
                    <td>{r.idRegion ?? "-"}</td>
                    <td>
                      <span style={{
                        display: "inline-block",
                        padding: "3px 8px",
                        borderRadius: 4,
                        background: statutColor[r.statut ?? 0] ?? "#6c757d",
                        color: "#fff",
                        fontSize: 12
                      }}>
                        {codeToRapportStatus(r.statut ?? 0)}
                      </span>
                    </td>
                    <td>{new Date(r.createdAt ?? "").toLocaleString("fr-FR")}</td>
                    <td>
                      <button
                        className="btn-icon"
                        style={{ fontSize: 20, lineHeight: 0 }}
                        onClick={e => {
                          const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                          setActionMenu(actionMenu?.id === r.idRapportGenere ? null : { id: r.idRapportGenere, x: rect.right, y: rect.bottom });
                        }}
                      >
                        &#x22EE;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Menu flottant d'actions */}
            {actionMenu && (
              <div
                ref={menuRef}
                style={{
                  position: "fixed",
                  top: actionMenu.y,
                  left: actionMenu.x - 150,
                  width: "150px",
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 9999,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <button onClick={() => deleteRapport(actionMenu.id)} style={{ padding: "8px", border: "none", background: "none", textAlign: "left" }}>Supprimer</button>
                <button onClick={() => openRapport(rapports.find(r => r.idRapportGenere === actionMenu.id)!)} style={{ padding: "8px", border: "none", background: "none", textAlign: "left" }}>Détails</button>
                {
                  (() => {
                    const r = rapports.find(rep => rep.idRapportGenere === actionMenu.id);
                    if (!r) return null;

                    // If status is 'créé' (0) show Valider for users with roleRank >= 2
                    if ((r.statut ?? 0) === 0 && user && (user.roleRank ?? 0) >= 2) {
                      return (
                        <button onClick={() => validerRapport(r)} style={{ padding: "8px", border: "none", background: "none", textAlign: "left" }}>Valider</button>
                      );
                    }

                    // After validation (status >= 1) show publish buttons
                    if ((r.statut ?? 0) >= 1 && user && (user.roleRank ?? 0) >= 2) {
                      return (
                        <>
                          <button onClick={() => publierRapport(r, "interne")} style={{ padding: "8px", border: "none", background: "none", textAlign: "left" }}>Publier Interne</button>
                          <button onClick={() => publierRapport(r, "public")} style={{ padding: "8px", border: "none", background: "none", textAlign: "left" }}>Publier Public</button>
                        </>
                      );
                    }

                    return null;
                  })()
                }
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <small className="text-muted">{pageData.length} rapports affichés sur {total}</small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {pageNumbers.map(num => (
                <li key={num} className={`page-item ${num === currentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>{num}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </main>
    </AuthentifiedPage>
  );
};

export default RapportsGeneresPage;